import {
  adminColumnSharedFields,
  exceptionAccountColumnDefinition,
  excludedCompanyCodesColumnDefinition,
  includeAllApplicableCountryTaxTypesColumnDefinition,
  connectionsColumnDefinition,
  bookToTaxDifferenceDescriptionColumnDefinition,
  exceptionBasisColumnDefinition,
  businessUnitsColumnDefinition,
} from '../../shared/columnDefinitions';

import BookToTaxDifferencesAdminColumnRenderer from './bookToTaxDifferencesAdminColumnRenderer';
import SelectCellEditor from '../../shared/gridEdit/selectCellEditor.component';
import { withIndicatorAndCustomInnerRendererCellRenderer } from '../../shared/gridEdit/validationMessagesIndicatorCellRenderer';
import {
  withIndicatorAndCustomInnerRendererCellRenderer as withIndicatorAndCustomInnerRendererCellRendererComponent,
} from '../../shared/gridEdit/validationMessagesIndicatorCellRenderer.component';

import { ALL_OPTION, exceptionBases, YNOptions } from '../../shared/constants';
import {
  nullableStringParser,
  floatParser,
  YNFlagParser,
  stringArrayParser,
  booleanParser,
} from '../../shared/gridEdit/utils/parsers';
import {
  getQuickFilterTextWithSkippingAddedRow,
} from '../../shared/gridEdit/quickFilterUtils';
import { GridMode } from '../../shared/gridEdit/useEditableGridLogic';
import {
  getGroupedCountryTaxTypeColumns,
  getCountryTaxTypeColumnDefinitionForBookToTaxDifferences,
  enabledFlagColumnDefinitionForBookToTaxDifferences,
  disallowedPercentageColumnDefinitionForBookToTaxDifferences,
  bookToTaxCodeDescriptionColumnDefinitionForBookToTaxDifferences,
} from '../../shared/columnDefinitions/bookToTaxDifferences';
import { getDropdownOptionsFromArray } from '../../shared/gridEdit/utils/utils';
import PercentageCellEditor from '@client-shared/agGrid/cellEditors/PercentageCellEditor';

const editableFactory = allowedModes => ({ data, context }) => {
  if (!context || !data) {
    return () => false;
  }
  return allowedModes.some(allowed => allowed === context.mode)
    && (
      (context.mode === GridMode.Add && data.meta.isAdded)
      || context.mode === GridMode.EditAll
      || (context.mode === GridMode.Edit && context.editedRowId === data.exceptionId)
    );
};

const getAdminColumnValue = ({ data }) => {
  if (!data) {
    return '';
  }
  if (data.meta.isUpdated) {
    return 'Update';
  }
  if (data.meta.isAdded) {
    return 'Add';
  }
  return '';
};

const getAdminColumnHeaderValue = ({ context }) =>
  context.mode === GridMode.Edit || context.mode === GridMode.EditAll || context.mode === GridMode.Add ? 'Add / Update' : '';

const adminColumn = ({ editRow, context }) => ({
  ...adminColumnSharedFields,
  field: 'exceptionId',
  cellRenderer: BookToTaxDifferencesAdminColumnRenderer,
  cellRendererParams: () => ({
    editRow,
    context,
  }),
  headerValueGetter: getAdminColumnHeaderValue,
  valueGetter: getAdminColumnValue,
  getQuickFilterText: getQuickFilterTextWithSkippingAddedRow,
  minWidth: 80,
});

const getEditableColumnDefinition = ({ setRowData, editable }) => ({
  editable,
  cellClassRules: {
    'disable-editing': args => !editable(args),
  },
  valueSetter: params => setRowData(params),
  filterParams: { newRowsAction: 'keep' },
});

const defaultColumnDefinition = {
  cellClassRules: {
    'disable-editing': () => true,
  },
  filterParams: { newRowsAction: 'keep' },
};

const defaultSelectCellEditorConfig = {
  closeOnSelect: true,
  filterOptions: true,
  openOnFocus: true,
  tabSelectsValue: true,
  inputProps: {
    className: `cell-editor-dropdown`,
  },
  onSelectResetsInput: false,
};

const getCountryTaxTypeColumnDefinition = ({ setRowData, countryTaxType, description }) => {
  const editable = editableFactory([GridMode.EditAll, GridMode.Add, GridMode.Edit]);
  return withIndicatorAndCustomInnerRendererCellRendererComponent({
    ...getCountryTaxTypeColumnDefinitionForBookToTaxDifferences({ countryTaxType, description }),
    editable: false,
    cellClassRules: {
      'disable-editing': args => (args.data.countryTaxTypes[0] === ALL_OPTION || !editable(args)),
    },
    cellRendererParams: {
      editable: args => (args.data.countryTaxTypes[0] !== ALL_OPTION && editable(args)),
    },
    valueParser: booleanParser,
    valueSetter: ({ node, newValue, colDef }) => {
      const countryTaxTypes = node.data.countryTaxTypes;
      setRowData({
        node,
        field: 'countryTaxTypes',
        newValue: newValue
          ? [...countryTaxTypes, colDef.field]
          : countryTaxTypes.filter(countryTaxType => colDef.field !== countryTaxType),
        colDef,
      });
    },
  });
};

const getBaseColumnDefinitions = params => [
  {
    ...enabledFlagColumnDefinitionForBookToTaxDifferences,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.EditAll, GridMode.Add, GridMode.Edit]),
    }),
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: {
      options: getDropdownOptionsFromArray(Object.values(YNOptions)),
      config: {
        ...defaultSelectCellEditorConfig,
      },
      autoSaveEnabled: true,
    },
    valueParser: YNFlagParser,
  },
  {
    ...exceptionAccountColumnDefinition,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.Add]),
    }),
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: {
      options: params.accounts,
      stopEditing: params.stopEditing,
      config: {
        ...defaultSelectCellEditorConfig,
      },
      autoSaveEnabled: true,
    },
    valueSetter: ({ node, newValue, colDef }) => {
      const newDescription = params.accounts.find(account => account.value === newValue);
      params.setRowData({ node, newValue, colDef });
      params.setRowData({
        node,
        field: 'description',
        newValue: newDescription ? newDescription.label : '',
        colDef,
      });
    },
  },
  {
    ...bookToTaxDifferenceDescriptionColumnDefinition,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.EditAll, GridMode.Add, GridMode.Edit]),
    }),
    cellEditorPopup: true,
  },
  {
    ...exceptionBasisColumnDefinition,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.Add]),
    }),
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: {
      options: exceptionBases.ALL,
      config: {
        ...defaultSelectCellEditorConfig,
      },
      autoSaveEnabled: true,
    },
  },
  {
    ...disallowedPercentageColumnDefinitionForBookToTaxDifferences,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.Add]),
    }),
    cellEditorPopup: true,
    cellEditor: PercentageCellEditor,
    valueParser: floatParser,
  },
  {
    ...bookToTaxCodeDescriptionColumnDefinitionForBookToTaxDifferences,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.EditAll, GridMode.Add, GridMode.Edit]),
    }),
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: {
      options: params.bookToTaxCodeDescriptions,
      stopEditing: params.stopEditing,
      withClearIcon: true,
      config: {
        ...defaultSelectCellEditorConfig,
        clearable: true,
      },
      autoSaveEnabled: true,
    },
    valueParser: nullableStringParser,
  },
  {
    ...connectionsColumnDefinition,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.EditAll, GridMode.Add, GridMode.Edit]),
    }),
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: {
      options: params.connections,
      withAllOption: true,
      truncatable: true,
      config: {
        multi: true,
        closeOnSelect: false,
      },
    },
    valueParser: stringArrayParser,
  },
  {
    ...businessUnitsColumnDefinition,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.EditAll, GridMode.Add, GridMode.Edit]),
    }),
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: {
      options: params.businessUnits,
      withAllOption: false,
      truncatable: true,
      config: {
        multi: true,
        closeOnSelect: false,
      },
    },
    valueParser: stringArrayParser,
  },
  {
    ...excludedCompanyCodesColumnDefinition,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.EditAll, GridMode.Add, GridMode.Edit]),
    }),
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: {
      options: params.companyCodes,
      truncatable: true,
      config: {
        multi: true,
        closeOnSelect: false,
      },
    },
    valueParser: stringArrayParser,
  },
  {
    ...includeAllApplicableCountryTaxTypesColumnDefinition,
    ...getEditableColumnDefinition({
      ...params,
      editable: editableFactory([GridMode.EditAll, GridMode.Add, GridMode.Edit]),
    }),
    cellEditor: SelectCellEditor,
    cellEditorPopup: true,
    cellEditorParams: {
      options: getDropdownOptionsFromArray(Object.values(YNOptions)),
      config: {
        ...defaultSelectCellEditorConfig,
      },
      autoSaveEnabled: true,
    },
    valueParser: YNFlagParser,
    valueSetter: ({ node, newValue, colDef }) => {
      params.setRowData({
        node,
        field: 'countryTaxTypes',
        newValue: newValue === YNOptions.Y ? [ALL_OPTION] : [],
        colDef,
      });
    },
  },
].map(columnDefinition => withIndicatorAndCustomInnerRendererCellRenderer(columnDefinition));

const getColumnDefinitions = params => ([
  {
    ...adminColumn({
      editRow: params.editRow,
      context: params.context,
    }),
    ...defaultColumnDefinition,
  },
  ...getBaseColumnDefinitions(params),
  ...getGroupedCountryTaxTypeColumns({
    ...params,
    countryTaxTypes: params.countryTaxTypes,
    showCountryCode: params.countryCode,
  }, getCountryTaxTypeColumnDefinition),
]);

export default getColumnDefinitions;
